<app-configuration-page
  title="Tax Configuration"
  description="Manage tax rates for your studio. Configure how taxes are calculated and applied to student payments."
  [breadcrumbs]="[{ label: 'Tax Configuration' }]">

  <mat-card class="configuration-card">
    <mat-card-content>
      <div>
        <mat-progress-spinner class="matProgressSpinnerCentered" *ngIf="showProgressSpinner" mode="indeterminate"></mat-progress-spinner>

        <form [formGroup]="taxConfigurationForm" *ngIf="!showProgressSpinner">
          <app-success-message [message]="this.successMessage"></app-success-message>
          <app-error-message [message]="this.errorMessage"></app-error-message>

          <div formArrayName="taxConfigurations">
            <mat-accordion>
              <mat-expansion-panel #taxPanel *ngFor="let taxConfig of taxConfigurations.controls; let i = index" [formGroupName]="i" class="tax-config-panel">
                <mat-expansion-panel-header>
                  <mat-panel-title>
                    {{ taxConfig.get('title').value || 'Untitled Configuration' }}
                  </mat-panel-title>
                  <mat-panel-description>
                    <span [innerHTML]="getHelpfulInfo(i)"></span>
                  </mat-panel-description>
                </mat-expansion-panel-header>

                <div class="tax-config-content">
                  <div class="tax-form-grid">
                    <div class="tax-form-panel">
                      <div class="tax-section-header compact">
                        <div>
                          <p class="section-eyebrow">Basic Information</p>
                          <p class="section-detail">Name, type & status</p>
                        </div>
                        <div class="section-state">
                          <mat-slide-toggle formControlName="field_tax_status" color="primary">
                            <span class="toggle-label">{{ taxConfig.get('field_tax_status').value ? 'Active' : 'Inactive' }}</span>
                          </mat-slide-toggle>
                        </div>
                      </div>

                      <div class="tax-section-body compact">
                        <mat-form-field class="full-width-field" appearance="outline">
                          <mat-label>Configuration Name</mat-label>
                          <input matInput formControlName="title" placeholder="e.g., California Sales Tax">
                          <mat-hint>Give this tax configuration a memorable name</mat-hint>
                        </mat-form-field>

                        <mat-form-field class="full-width-field" appearance="outline">
                          <mat-label>Tax Type</mat-label>
                          <mat-select formControlName="field_tax_type">
                            <mat-option *ngFor="let type of taxTypes" [value]="type.value">
                              {{ type.label }}
                            </mat-option>
                          </mat-select>
                          <mat-hint>Select the type of tax applicable to your location</mat-hint>
                        </mat-form-field>

                        <div class="toggle-group">
                          <mat-slide-toggle formControlName="field_is_tax_inclusive" color="primary">
                            <span class="toggle-label">Tax-Inclusive Pricing</span>
                          </mat-slide-toggle>
                          <p class="toggle-helper">The tax is baked into the listed price (this is disabled by default for most U.S. studios, which are tax-exclusive).</p>
                        </div>
                      </div>
                    </div>

                    <div class="tax-form-panel">
                      <div class="tax-section-header compact">
                        <div>
                          <p class="section-eyebrow">Calculation & Dates</p>
                          <p class="section-detail">Rates, rounding, and validity</p>
                        </div>
                      </div>

                      <div class="tax-section-body compact">
                        <div class="calc-grid compact">
                          <mat-form-field class="full-width-field" appearance="outline">
                            <mat-label>How should tax be calculated?</mat-label>
                            <mat-select formControlName="field_calculation_method" (selectionChange)="onCalculationMethodChange(i)">
                              <mat-option *ngFor="let method of calculationMethods" [value]="method.value">
                                {{ method.label }}
                              </mat-option>
                            </mat-select>
                            <mat-hint>Choose how to calculate the tax amount</mat-hint>
                          </mat-form-field>

                          <mat-form-field class="full-width-field" appearance="outline">
                            <mat-label>{{ getTaxValueLabel(i) }}</mat-label>
                            <input matInput formControlName="field_tax_value" type="number" step="0.001" [placeholder]="getTaxValuePlaceholder(i)">
                            <mat-hint>{{ taxConfig.get('field_calculation_method').value === 'PERCENTAGE' ? 'Enter as a number (e.g., 8.5 for 8.5%)' : 'Enter the fixed dollar amount' }}</mat-hint>
                          </mat-form-field>
                        </div>

                        <mat-form-field class="full-width-field" appearance="outline">
                          <mat-label>Rounding</mat-label>
                          <mat-select formControlName="field_rounding_rule">
                            <mat-option *ngFor="let rule of roundingRules" [value]="rule.value">
                              {{ rule.label }}
                            </mat-option>
                          </mat-select>
                          <mat-hint>How to round the calculated tax amount</mat-hint>
                        </mat-form-field>

                        <div class="effective-grid compact">
                          <mat-form-field class="full-width-field" appearance="outline">
                            <mat-label>Start Date</mat-label>
                            <input matInput appFlatpickrDatetime [enableTime]="false" formControlName="field_effective_tax_start_date" placeholder="Leave blank for no start date">
                            <mat-hint>When this tax configuration becomes active</mat-hint>
                          </mat-form-field>
                          <mat-form-field class="full-width-field" appearance="outline">
                            <mat-label>End Date</mat-label>
                            <input matInput appFlatpickrDatetime [enableTime]="false" formControlName="field_effective_tax_end_date" placeholder="Leave blank for no end date">
                            <mat-hint>When this tax configuration expires</mat-hint>
                          </mat-form-field>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Actions -->
                  <div class="row mt-4">
                    <div class="col-12">
                      <div class="config-action-row">
                        <button mat-flat-button color="primary" class="action-btn primary-action" (click)="triggerSave(taxPanel)">
                          <mat-icon>save</mat-icon>
                          Save Configuration
                        </button>
                        <button mat-flat-button color="warn" class="action-btn danger-action" (click)="removeTaxConfiguration(i)">
                          <mat-icon>delete</mat-icon>
                          Delete This Configuration
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </mat-expansion-panel>
            </mat-accordion>
          </div>

          <button mat-raised-button color="primary" (click)="addTaxConfiguration()" class="mt-3">
            <mat-icon>add</mat-icon>
            Add New Tax Configuration
          </button>
        </form>
      </div>
    </mat-card-content>

    <mat-card-actions align="end" class="p-3">
      <button type="button" [disabled]="!taxConfigurationForm.valid" (click)="actionType === 'create' ? onSubmit() : onUpdate()" mat-raised-button color="accent" class="px-4">
        <mat-icon>save</mat-icon>
        {{ actionType === 'create' ? 'Save' : 'Save Changes' }}
      </button>
    </mat-card-actions>
  </mat-card>

</app-configuration-page>
